import React, {useEffect, useState} from "react";
import {RestaurantType} from "./RegStepOne";
import {Button, Input, message} from "antd";
import {env} from "../../env/env";
import ReCAPTCHA from "react-google-recaptcha";
import MembersApi from "../../api/MembersApi";
import Storage from "../../util/Storage";
import {GoogleMember, MemberDto, RegisterDto, RegisterMemberDto} from "../../generated/account-client";
import {generatePassword, getRestaurantTypeLabel, isValidEmail, isValidPhone, isValidZip} from "../../util/utils";
import UsersApi from "../../api/UsersApi";
import {error} from "../../service/MessageService";
import {Button as CButton} from "../account/misc/Button";
import {LeftOutlined} from "@ant-design/icons";
import {RegMemberDto} from "../../generated/dashboard-client";
import CustomSelect from "./CustomSelect";
import Tos from "./Tos";
import {RegOrLoginDecision} from "./RegStepZero";
import {LogRegLogo} from "../util/LogRegLogo";
import AppApi from "../../api/AppApi";

interface RegStepTwoProps {
  type: RestaurantType;
  onRegisterSuccess: (member: RegMemberDto, internalId: number) => void;
  inviteMode: boolean;
  inviteId: string | undefined;
  goBack: () => void;
  next: string | null;
  decision: RegOrLoginDecision;
  email: string;
  onEmailChange: (email: string) => void;
  password: string;
  onPasswordChange: (password: string) => void;
  onSignIn: () => void;
  popmenuFlow: boolean;
}

const posSystems = ["Square", "Toast", "SpotOn", "Clover", "Lightspeed", "Oracle Micros", "TouchBistro", "Rezku", "Arryved", "Aloha", "Revel", "Lavu", "No POS", "Other"];
const foodDistributors = ["Sysco", "US Foods", "Shamrock Foods", "Performance Food Group", "Gordon Food Service", "Harbor Foods", "The H.T. Hackney Co.", "Maines Food Service", "Other"];
const digitalSignageSolutions = ["USB Stick", "Yodeck", "Optisigns", "Raydiant", "Screenly", "Delphi", "Screencloud", "Mood Media", "Look", "MangoSigns", "Novisigns", "Pickcel", "Eyecatch", "DoPublicity", "Other"];

export default function RegStepTwo({type, inviteMode, inviteId, onRegisterSuccess, goBack, next, decision, email, onEmailChange, password, onPasswordChange, onSignIn, popmenuFlow}: RegStepTwoProps) {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [business, setBusiness] = useState("");
  const [phone, setPhone] = useState("");
  const [zip, setZip] = useState("");
  const [confirm, setConfirm] = useState("");
  const [captcha, setCaptcha] = useState(false);
  const [pos, setPos] = useState(null as string | null);
  const [posOther, setPosOther] = useState(null as string | null);
  const [fsp, setFsp] = useState(null as string | null);
  const [fspOther, setFspOther] = useState(null as string | null);
  const [dss, setDss] = useState(null as string | null);
  const [dssOther, setDssOther] = useState(null as string | null);
  const [loading, setLoading] = useState(false);

  const [isFirstErr, setFirstErr] = useState(false);
  const [isLastErr, setLastErr] = useState(false);
  const [isBusinessErr, setBusinessErr] = useState(false);
  const [isEmailErr, setEmailErr] = useState(false);
  const [isPhoneErr, setPhoneErr] = useState(false);
  const [isZipErr, setZipErr] = useState(false);
  const [isPassErr, setPassErr] = useState(false);
  const [isConfirmErr, setConfirmErr] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);

  const [isValid, setValid] = useState(false);

  const [member, setMember] = useState({} as MemberDto);
  const [googleRegister, setGoogleRegister] = useState(false);

  useEffect(() => {
    setGoogleRegister(decision.type === 'google');
  }, [decision]);

  useEffect(() => {
    if (!inviteId) return;
    UsersApi.getUserInvite(inviteId)
      .then((resp) => {
        setMember(resp.data);
      })
      .catch((error) => {
        if (error?.response?.data?.message === "invitation.expired") {
          setLinkExpired(true);
        }
      });
  }, [inviteId]);

  useEffect(() => {
    let valid = true;
    if (!captcha) {
      valid = false;
    }

    if (inviteMode) {
      if (!first || !last || isPassErr || password !== confirm) {
        valid = false;
      }
    } else {
      if (googleRegister) {
        if (!decision.gMember?.firstName?.trim() || !decision.gMember?.lastName?.trim() || !business?.trim() || !phone?.trim() || !zip?.trim() || !decision.gMember.email?.trim() ||
          isPhoneErr || isZipErr) {
          valid = false;
        }
      } else {
        if (!first?.trim() || !last?.trim() || !business?.trim() || !phone?.trim() || !zip?.trim() || !email?.trim() || !password?.trim() ||
          isPhoneErr || isZipErr || isPassErr) {
          valid = false;
        }
      }
    }

    setValid(valid);
  }, [inviteMode, captcha, first, last, phone, zip, email, password, confirm, business, isPhoneErr, isZipErr, isPassErr, type, googleRegister, decision.gMember]);

  function register() {
    if (!isValid) return;
    if (email && !isValidEmail(email)) return;
    if (phone && !isValidPhone(phone)) return;
    if (zip && !isValidZip(zip)) return;

    if (inviteMode) {
      if (googleRegister) {
        registerTeamUser(buildGoogleUser(decision.gMember!!));
      } else {
        registerTeamUser();
      }
    } else {
      if (googleRegister) {
        registerAccount(buildGoogleMember(decision.gMember!!));
      } else {
        registerAccount();
      }
    }
  }

  function registerTeamUser(customUser?: RegisterDto) {
    setLoading(true);
    const user = customUser || buildUser();
    UsersApi.register(user)
      .then(async () => {
        setLoading(false);
        await performLogin(member.email ?? "", user.password!.trim());
      })
      .catch((err) => {
        setLoading(false);
        error(err?.response?.data?.message || err.message);
      });
  }

  function reward() {
    if (typeof  window.rewardful !== "undefined") {
      window.rewardful("convert", { email });
    }
  }

  function buildUser() {
    return {
      first,
      last,
      password: password.trim(),
      confirm,
      invitation: inviteId,
    };
  }

  function buildMember() {
    return {
      first, last, email, phone, zip, password: password.trim(),
      referral: window.Rewardful?.referral,
      business: business || "Your location",
      restaurantType: getRestaurantTypeLabel(type),
      numLocations: -1,
      source: popmenuFlow ? 'popmenu' : ''
    } as RegisterMemberDto;
  }

  function buildGoogleMember(gMember: GoogleMember) {
    const password = generatePassword();
    return {
      first: gMember.firstName,
      last: gMember.lastName,
      email: gMember.email,
      phone: phone,
      zip: zip,
      password,
      referral: window.Rewardful?.referral,
      business: business || "Your location",
      restaurantType: getRestaurantTypeLabel(type),
      numLocations: -1,
      source: popmenuFlow ? 'popmenu' : ''
    } as RegisterMemberDto;
  }

  function buildGoogleUser(gMember: GoogleMember) {
    const password = generatePassword();
    return {
      first: gMember.firstName,
      last: gMember.lastName,
      password,
      confirm: password,
      invitation: inviteId
    };
  }

  function registerAccount(customMember?: RegisterMemberDto) {
    setLoading(true);

    const member = customMember || buildMember();

    MembersApi
      .registerMember(member)
      .then(async resp => {
        reward();
        const {id, internalId} = resp.data;

        await performLogin(member.email!, member.password!.trim(), id, internalId, async () => {
          const posParam = pos === 'Other' ? posOther : pos;
          const dssParam = dss === 'Other' ? dssOther : dss;
          const fspParam = fsp === 'Other' ? fspOther : fsp;

          await MembersApi.saveQuestionnaire(member.email!, {
            pos: posParam || '',
            dss: dssParam || '',
            fsp: fspParam || ''
          })
        });
      }).catch((err) => {
        const key = err.response.data.message;
        setLoading(false);
        message.error(key === "email.in.use" ? "This email is in use." : "Something went wrong.");
      });
  }

  async function performLogin(email: string, password: string = "", id: string = "", internalId: number = 0, fnc?: () => Promise<void>) {
    setLoading(true);
    const loginResp = await AppApi.login({username: email, password: password.trim()});
    const resp = loginResp.data;

    if (resp.token) {
      Storage.setToken(resp.token);

      if (fnc) {
        await fnc();
      }

      setLoading(false);
      onRegisterSuccess({
        name: `${first} ${last}`,
        id, email, business, zip, phone,
        restaurantType: getRestaurantTypeLabel(type),
        numLocations: -1
      }, internalId);
    }
  }

  function isValidPassword(pass: string) {
    return /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[~`*()_\-+={}[\]|:;'<>,.?/!@#$%^&*])(?=.{8,})/.test(pass);
  }

  function onPassChange(password: string) {
    onPasswordChange(password.trim());
    setPassErr(!isValidPassword(password.trim()));
    setConfirmErr(password.trim() !== confirm.trim());
  }

  function onConfirmChange(confirm: string) {
    setConfirm(confirm.trim());
    setConfirmErr(password.trim() !== confirm.trim());
  }

  return (
    <div className={"step-fields step full"}>
      {!inviteMode && <button className={"back"} onClick={goBack}><LeftOutlined/></button>}

      {linkExpired && (
        <div className={"form expired"}>
          <LogRegLogo/>
          <h1 className="emoji">&#128532;</h1>
          <h1>
            <b>Oh no! This link is expired.</b>
          </h1>
        </div>
      )}

      {!linkExpired && (
      <div className={"form"}>
        <h2>{inviteMode ? "Set up your login" : "Create Account"}</h2>
        {!inviteMode && <h6>Already have an account? <CButton type={'LinkPrimary'} className={'sign-in-link'} onClick={onSignIn}>Sign in</CButton></h6>}

        <div className={"form"}>
          {!inviteMode && !googleRegister && <div className={"blk"}>
            <Input type={"email"} placeholder={"Email"} value={email} size={"large"}
                   className={isEmailErr ? "err" : ""}
                   onChange={ev => onEmailChange(ev.target.value)}
                   onBlur={ev => setEmailErr(!isValidEmail(ev.target.value.trim()))}/>
            {isEmailErr && <label className={"err"}>Enter a valid email address.</label>}
          </div>}

          {!googleRegister && <div className={"group vertical"}>
            <div>
              <div className={"blk"}>
                <Input.Password type={"password"} placeholder={"Password"} value={password} size={"large"}
                                onChange={ev => onPassChange(ev.target.value)}/>
              </div>

              <div className={"blk"}>
                <Input.Password type={"password"} placeholder={"Retype Password"} value={confirm} size={"large"}
                                onChange={ev => onConfirmChange(ev.target.value)}/>
              </div>
            </div>
            <div className={'msg'}>
              <label className={`pass ${isPassErr ? 'err' : ''}`}>8+ characters with a mix of letters, numbers and symbols (no \ or ").</label>
              {isConfirmErr && <label className={`pass err`}>Your passwords don't match.</label>}
            </div>
          </div>}

          {!googleRegister && <div className={!inviteMode ? "group" : ""}>
            <div className={"blk"}>
              <Input type={"text"} placeholder={"First Name"} value={first} size={"large"}
                     className={isFirstErr ? "err" : ""}
                     onChange={ev => {
                       setFirst(ev.target.value);
                       setFirstErr(!ev.target.value.trim());
                     }}/>
              {isFirstErr && <label className={"err"}>Enter your first name.</label>}
            </div>
            <div className={"blk"}>
              <Input type={"text"} placeholder={"Last Name"} value={last} size={"large"}
                     className={isLastErr ? "err" : ""}
                     onChange={ev => {
                       setLast(ev.target.value);
                       setLastErr(!ev.target.value.trim());
                     }}/>
              {isLastErr && <label className={"err"}>Enter your last name.</label>}
            </div>
          </div>}

          {!inviteMode &&
            <div className={"blk"}>
              <Input type={"text"}
                     placeholder={type === RestaurantType.Personal ? "School name" : "Restaurant or Business Name"}
                     value={business} size={"large"}
                     className={isBusinessErr ? "err" : ""}
                     onChange={ev => {
                       setBusiness(ev.target.value);
                       setBusinessErr(!ev.target.value.trim());
                     }}/>
              {isBusinessErr && <label className={"err"}>Enter your {type === RestaurantType.Personal ? "school" : "business"} name.</label>}
            </div>}


          <div className={"group"}>
            <div className={"blk"}>
              <Input type={"text"} placeholder={"Best Contact Phone"} value={phone} size={"large"}
                     className={isPhoneErr ? "err" : ""}
                     onChange={ev => setPhone(ev.target.value)}
                     onBlur={ev => setPhoneErr(!isValidPhone(ev.target.value))}/>
              {isPhoneErr && <label className={"err"}>Enter your phone number.</label>}
            </div>

            <div className={"blk"}>
              <Input type={"text"} placeholder={"Restaurant Zip Code"} value={zip} size={"large"}
                     className={isZipErr ? "err" : ""}
                     onChange={ev => setZip(ev.target.value)}
                     onBlur={ev => setZipErr(!isValidZip(ev.target.value))}/>
              {isZipErr && <label className={"err"}>Enter your zip code.</label>}
            </div>
          </div>

          <div className={'blk'}>
            <CustomSelect values={posSystems} placeholder={"Select POS Provider"} value={pos} setValue={setPos} otherValue={posOther}
                          setOtherValue={setPosOther}/>
          </div>
          <div className={'blk'}>
            <CustomSelect values={foodDistributors} placeholder={"Select Food Service Provider"} value={fsp} setValue={setFsp} otherValue={fspOther}
                          setOtherValue={setFspOther}/>
          </div>
          <div className={'blk'}>
            <CustomSelect values={digitalSignageSolutions} placeholder={"Select Digital Menu Provider"} value={dss} setValue={setDss}
                          otherValue={dssOther} setOtherValue={setDssOther}/>
          </div>
          <div className={'blk recaptcha'}>
            <ReCAPTCHA size="normal" sitekey={env.reCaptchaKey} onChange={() => setCaptcha(true)}/>
          </div>
        </div>

        <footer>
          <Tos/>
          <Button
            size={"large"}
            className={"primary"}
            onClick={register}
            loading={loading}
            disabled={!isValid}>
            Next
          </Button>
        </footer>
      </div>
      )}
    </div>
  );
}
